import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Kompakte Statusanzeige für die Durchschnittsarbeitszeit nach § 3 Satz 2 ArbZG.
 * 
 * Zeigt: "Ø Arbeitszeit (24 Wochen): 7,9 h / 8,0 h"
 * Farben: grün (OK), gelb (nahe Grenze), rot (überschritten)
 */
const AverageWorkTimeStatus = ({ userId, referenceDate, compact = false, showDetails = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (userId) params.append('user_id', userId);
        if (referenceDate) params.append('reference_date', referenceDate);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/average-working-time?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden des Status');
        }
        
        const data = await response.json();
        setStatus(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [userId, referenceDate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <div className="w-4 h-4 border-2 border-border border-t-zinc-400 rounded-full animate-spin" />
        <span>Lade Ø-Arbeitszeit...</span>
      </div>
    );
  }

  if (error || !status) {
    return null; // Fehler still ignorieren für bessere UX
  }

  // Bestimme Status-Farbe
  const getStatusColor = () => {
    if (!status.is_compliant) return 'text-red-600 dark:text-red-400';
    const headroomPerDay = status.limit - status.average_daily_hours;
    if (headroomPerDay < 0.25) return 'text-yellow-400';
    return 'text-emerald-600 dark:text-emerald-400';
  };

  const getStatusIcon = () => {
    if (!status.is_compliant) return <AlertCircle className="w-4 h-4" />;
    const headroomPerDay = status.limit - status.average_daily_hours;
    if (headroomPerDay < 0.25) return <AlertTriangle className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getStatusBg = () => {
    if (!status.is_compliant) return 'bg-red-500/10 border-red-500/20';
    const headroomPerDay = status.limit - status.average_daily_hours;
    if (headroomPerDay < 0.25) return 'bg-yellow-500/10 border-yellow-500/20';
    return 'bg-emerald-500/10 border-emerald-500/20';
  };

  const periodLabel = status.reference_mode === '24_weeks' ? '24 Wochen' : '6 Monate';

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${getStatusColor()} ${getStatusBg()} border`}>
        {getStatusIcon()}
        <span>Ø {status.average_daily_hours.toFixed(1)} h / {status.limit.toFixed(0)} h</span>
      </div>
    );
  }

  return (
    <div className={`p-3 rounded-lg border ${getStatusBg()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={getStatusColor()}>{getStatusIcon()}</span>
          <div>
            <div className={`font-medium ${getStatusColor()}`}>
              Ø Arbeitszeit ({periodLabel}): {status.average_daily_hours.toFixed(2)} h / {status.limit.toFixed(1)} h
            </div>
            {showDetails && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {status.total_worked_hours.toFixed(1)} h auf {status.workdays_count} Werktage
              </div>
            )}
          </div>
        </div>
        
        {!status.is_compliant && (
          <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            § 3 ArbZG
          </div>
        )}
      </div>
      
      {showDetails && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Zeitraum: {status.period_start} bis {status.period_end}</span>
            <span>Puffer: {status.headroom_hours.toFixed(1)} h</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Warnhinweis für die Zeiterfassung, wenn ein Eintrag den Durchschnitt beeinflusst.
 */
export const AverageImpactWarning = ({ date, hours, onImpactCalculated }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkImpact = async () => {
      if (!date || !hours || hours <= 0) {
        setImpact(null);
        return;
      }

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ date, hours: hours.toString() });
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/average-working-time/check-impact?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setImpact(data);
          if (onImpactCalculated) {
            onImpactCalculated(data);
          }
        }
      } catch (err) {
        console.error('Error checking impact:', err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce
    const timeout = setTimeout(checkImpact, 500);
    return () => clearTimeout(timeout);
  }, [date, hours, onImpactCalculated]);

  if (loading || !impact) return null;
  
  if (!impact.warning_message) return null;

  const isExceeded = impact.would_exceed;
  const bgColor = isExceeded ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30';
  const textColor = isExceeded ? 'text-red-600 dark:text-red-400' : 'text-yellow-400';
  const Icon = isExceeded ? AlertCircle : AlertTriangle;

  return (
    <div className={`p-3 rounded-lg border ${bgColor} mt-2`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 ${textColor}`} />
        <div className="flex-1">
          <div className={`text-sm ${textColor}`}>{impact.warning_message}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Neuer Ø: {impact.average_daily_hours_after.toFixed(2)} h/Werktag 
            (Puffer: {impact.headroom_hours_after.toFixed(1)} h)
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Team-Übersicht für Admins/Vorgesetzte
 */
export const TeamAverageViolationsList = ({ referenceDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (referenceDate) params.append('reference_date', referenceDate);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/average-working-time/team?${params}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          if (response.status === 403) {
            setError('Keine Berechtigung');
          } else {
            throw new Error('Fehler beim Laden');
          }
          return;
        }
        
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [referenceDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 text-muted-foreground">
        <div className="w-5 h-5 border-2 border-border border-t-zinc-400 rounded-full animate-spin mr-2" />
        Lade Team-Daten...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-muted-foreground text-center text-sm">
        {error}
      </div>
    );
  }

  if (!data || data.employees_at_risk.length === 0) {
    return (
      <div className="p-4 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" />
        <div className="text-muted-foreground">Alle Mitarbeiter im grünen Bereich</div>
        <div className="text-xs text-muted-foreground mt-1">
          Keine Überschreitungen der Ø-Arbeitszeit
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span className="flex items-center gap-1">
          <AlertTriangle className="w-4 h-4" />
          {data.total_count} Mitarbeiter mit kritischem Ø
        </span>
        <span className="text-xs">Stand: {data.reference_date}</span>
      </div>
      
      <div className="space-y-1">
        {data.employees_at_risk.map((emp) => (
          <div
            key={emp.user_id}
            className={`flex items-center justify-between p-2 rounded-lg border ${
              emp.is_compliant 
                ? 'bg-yellow-500/5 border-yellow-500/20' 
                : 'bg-red-500/10 border-red-500/20'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${emp.is_compliant ? 'bg-yellow-400' : 'bg-red-400'}`} />
              <span className="text-foreground text-sm">{emp.user_name}</span>
            </div>
            <div className="text-right">
              <div className={`text-sm font-medium ${emp.is_compliant ? 'text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                {emp.average_daily_hours.toFixed(2)} h / {emp.limit.toFixed(0)} h
              </div>
              <div className="text-xs text-muted-foreground">
                Puffer: {emp.headroom_hours.toFixed(1)} h
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AverageWorkTimeStatus;
